import {
  ProductFormDraftSnapshot,
  ProductFormDraftSummary,
} from '@features/catalog/models/product-form-draft.models';

export const PRODUCT_FORM_DRAFT_VERSION = 7;
export const PRODUCT_FORM_DRAFT_INDEX_KEY = 'catalog.product-form.drafts';
export const PRODUCT_FORM_DRAFT_INDEX_MAX = 12;

export function isProductFormDraftStorageAvailable(storage?: Storage | null): boolean {
  const target = storage ?? (typeof window !== 'undefined' ? window.localStorage : null);
  return !!target;
}

export function parseProductFormDraftJson<T>(raw: string | null): T | null {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function isValidProductFormDraftVersion(version: unknown): version is typeof PRODUCT_FORM_DRAFT_VERSION {
  return version === PRODUCT_FORM_DRAFT_VERSION;
}

export function readProductFormDraftSummaries(storage: Storage): ProductFormDraftSummary[] {
  const parsed = parseProductFormDraftJson<unknown>(storage.getItem(PRODUCT_FORM_DRAFT_INDEX_KEY));
  return Array.isArray(parsed) ? (parsed as ProductFormDraftSummary[]) : [];
}

export function writeProductFormDraftSummaries(
  summaries: ProductFormDraftSummary[],
  storage: Storage
): void {
  storage.setItem(PRODUCT_FORM_DRAFT_INDEX_KEY, JSON.stringify(summaries));
}

export function pruneProductFormDraftIndex(
  summaries: ProductFormDraftSummary[],
  max: number = PRODUCT_FORM_DRAFT_INDEX_MAX
): ProductFormDraftSummary[] {
  return summaries.slice(0, max);
}

export function readProductFormDraftSnapshot(
  key: string,
  storage: Storage
): ProductFormDraftSnapshot | null {
  const parsed = parseProductFormDraftJson<ProductFormDraftSnapshot>(storage.getItem(key));
  if (!parsed) {
    return null;
  }

  if (!isValidProductFormDraftVersion(parsed.version)) {
    removeProductFormDraft(key, storage);
    return null;
  }

  return parsed;
}

export function writeProductFormDraftSnapshot(
  key: string,
  snapshot: ProductFormDraftSnapshot,
  storage: Storage
): void {
  storage.setItem(key, JSON.stringify(snapshot));
}

export function upsertProductFormDraftSummary(
  summary: ProductFormDraftSummary,
  storage: Storage
): void {
  const summaries = readProductFormDraftSummaries(storage).filter((draft) => draft.key !== summary.key);
  summaries.unshift(summary);
  writeProductFormDraftSummaries(pruneProductFormDraftIndex(summaries), storage);
}

export function writeProductFormDraft(
  key: string,
  snapshot: ProductFormDraftSnapshot,
  storage: Storage
): void {
  writeProductFormDraftSnapshot(key, snapshot, storage);
  upsertProductFormDraftSummary(snapshot.summary, storage);
}

export function removeProductFormDraft(key: string, storage: Storage): void {
  storage.removeItem(key);
  const remaining = readProductFormDraftSummaries(storage).filter((draft) => draft.key !== key);
  writeProductFormDraftSummaries(remaining, storage);
}
